import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axiosClient from '../api/axiosClient';
import toast from 'react-hot-toast';
import {
  Server,
  Database,
  ShieldCheck,
  CheckCircle2,
  AlertTriangle,
  Lock,
  User,
  Mail,
  Key,
  Globe,
  ArrowRight,
  ArrowLeft,
  RefreshCw,
  Sparkles,
  Layers,
  Cpu,
  Eye,
  EyeOff,
  ExternalLink,
} from 'lucide-react';

export default function InstallPage() {
  const navigate = useNavigate();

  // Wizard State
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [installing, setInstalling] = useState(false);
  const [statusData, setStatusData] = useState(null);
  const [dbTesting, setDbTesting] = useState(false);
  const [dbTestResult, setDbTestResult] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // Form State
  const [formData, setFormData] = useState({
    siteName: 'FAJTech Managed IT & Cybersecurity Services',
    tagline: 'Premier Managed IT & Cybersecurity Solutions',
    adminName: 'Enterprise Admin',
    adminEmail: 'admin@fajtech.com',
    adminPassword: '',
    confirmPassword: '',
    contactEmail: 'info@fajtech.com',
    contactPhone: '+1 (800) 555-0199',
    seedDemoData: true,
  });

  // Installation result
  const [installResult, setInstallResult] = useState(null);

  // Fetch status on load
  const fetchStatus = async () => {
    setLoading(true);
    setErrorMsg('');
    try {
      const res = await axiosClient.get('/install/status');
      setStatusData(res.data);
      if (res.data.siteName) {
        setFormData((prev) => ({
          ...prev,
          siteName: res.data.siteName || prev.siteName,
        }));
      }
    } catch (err) {
      console.error('Failed to fetch install status:', err);
      setErrorMsg(
        err.response?.data?.error ||
          'Failed to communicate with the backend API. Please ensure the server is active.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  // Test DB connection manually
  const handleTestDb = async () => {
    setDbTesting(true);
    setDbTestResult(null);
    try {
      const res = await axiosClient.post('/install/test-db');
      setDbTestResult({
        success: true,
        message: `Database ping successful (${res.data.latencyMs}ms)`,
      });
      toast.success('Database connection verified!');
    } catch (err) {
      setDbTestResult({
        success: false,
        message: err.response?.data?.message || 'Database connection test failed.',
      });
      toast.error('Database connection failed');
    } finally {
      setDbTesting(false);
    }
  };

  // Run install
  const handleRunInstall = async (e) => {
    e?.preventDefault();
    setErrorMsg('');

    if (!formData.adminEmail || !formData.adminPassword) {
      toast.error('Please enter administrator email and password');
      return;
    }

    if (formData.adminPassword.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }

    if (formData.adminPassword !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setInstalling(true);
    try {
      const res = await axiosClient.post('/install/setup', {
        ...formData,
        force: statusData?.installed ? true : false,
      });

      setInstallResult(res.data);
      setCurrentStep(4);
      toast.success('CMS installed and configured successfully!');
    } catch (err) {
      console.error('Installation failed:', err);
      const msg = err.response?.data?.error || 'Installation encountered an unexpected error.';
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setInstalling(false);
    }
  };

  // Password strength calculation
  const getPasswordStrength = () => {
    const p = formData.adminPassword;
    if (!p) return { score: 0, label: 'None', color: 'bg-slate-700' };
    let score = 0;
    if (p.length >= 8) score += 1;
    if (/[A-Z]/.test(p)) score += 1;
    if (/[0-9]/.test(p)) score += 1;
    if (/[^A-Za-z0-9]/.test(p)) score += 1;

    if (score <= 1) return { score: 1, label: 'Weak', color: 'bg-red-500' };
    if (score === 2) return { score: 2, label: 'Fair', color: 'bg-amber-500' };
    if (score === 3) return { score: 3, label: 'Good', color: 'bg-blue-500' };
    return { score: 4, label: 'Strong', color: 'bg-emerald-500' };
  };

  const strength = getPasswordStrength();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-4 text-white">
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin"></div>
            <Sparkles className="w-6 h-6 text-blue-400 absolute inset-0 m-auto animate-pulse" />
          </div>
          <h2 className="text-xl font-bold tracking-wide">Inspecting System Environment</h2>
          <p className="text-sm text-slate-400">Verifying database and CMS setup status...</p>
        </div>
      </div>
    );
  }

  // Already installed screen (if user doesn't explicitly want to reconfigure)
  if (statusData?.installed && currentStep === 1 && !installResult) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-4 relative overflow-hidden">
        {/* Background glow effects */}
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-blue-600/15 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-indigo-600/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative max-w-xl w-full bg-slate-900/90 backdrop-blur-xl border border-slate-800 rounded-3xl p-8 sm:p-10 shadow-2xl">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-2xl text-emerald-400">
              <ShieldCheck className="w-8 h-8" />
            </div>
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-emerald-400 bg-emerald-950/60 px-2.5 py-0.5 rounded-full border border-emerald-800/60">
                System Active
              </span>
              <h1 className="text-2xl font-black text-white tracking-tight mt-1">
                CMS is Already Installed
              </h1>
            </div>
          </div>

          <p className="text-slate-300 text-sm leading-relaxed mb-6">
            The FAJTech CMS has already been configured and initialized. The Super Administrator account
            and database structures are fully active. For security, the installer is locked.
          </p>

          <div className="bg-slate-950/70 border border-slate-800/80 rounded-2xl p-5 mb-8 space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-400">Site Title:</span>
              <span className="font-semibold text-slate-200">{statusData.siteName || 'FAJTech'}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-400">Database Status:</span>
              <span className="inline-flex items-center gap-1.5 text-emerald-400 font-medium">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping"></span>
                Connected
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-400">Super Administrators:</span>
              <span className="font-medium text-slate-200">{statusData.adminCount} Registered</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-400">Environment:</span>
              <span className="font-mono text-xs text-blue-400 uppercase bg-blue-950/40 px-2 py-0.5 rounded border border-blue-900/40">
                {statusData.system?.environment}
              </span>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => navigate('/admin/login')}
              className="flex-1 py-3.5 px-6 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-semibold text-sm shadow-lg shadow-blue-600/30 flex items-center justify-center gap-2 transition-all"
            >
              <Lock className="w-4 h-4" />
              Go to Admin Login
            </button>
            <Link
              to="/"
              className="py-3.5 px-6 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white font-semibold text-sm border border-slate-700 flex items-center justify-center gap-2 transition-all"
            >
              <Globe className="w-4 h-4" />
              View Website
            </Link>
          </div>

          <div className="mt-8 pt-6 border-t border-slate-800/80 flex items-center justify-between text-xs text-slate-400">
            <span>Need to re-provision credentials?</span>
            <button
              onClick={() => setCurrentStep(2)}
              className="text-blue-400 hover:text-blue-300 font-medium underline transition"
            >
              Reconfigure System
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white relative selection:bg-blue-500 selection:text-white py-12 px-4 sm:px-6">
      {/* Background ambient light */}
      <div className="fixed top-0 left-1/2 -translate-x-1/2 w-[800px] h-[350px] bg-gradient-to-b from-blue-600/20 via-indigo-600/10 to-transparent blur-3xl pointer-events-none" />

      <div className="max-w-3xl mx-auto relative z-10">
        {/* Header Branding */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-400 text-xs font-semibold uppercase tracking-wider mb-4">
            <Sparkles className="w-3.5 h-3.5" />
            FAJTech Enterprise Installer
          </div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-white">
            CMS Setup & Installation
          </h1>
          <p className="mt-2 text-sm sm:text-base text-slate-400 max-w-lg mx-auto">
            Welcome to the FAJTech guided installer. Set up your database connection, site identity, and Super Administrator account.
          </p>
        </div>

        {/* Stepper Progress Bar */}
        <div className="mb-8 bg-slate-900/80 backdrop-blur border border-slate-800/80 rounded-2xl p-4 shadow-xl">
          <div className="grid grid-cols-4 gap-2">
            {[
              { num: 1, title: 'Readiness', icon: Cpu },
              { num: 2, title: 'Database', icon: Database },
              { num: 3, title: 'Site & Admin', icon: User },
              { num: 4, title: 'Complete', icon: CheckCircle2 },
            ].map((step) => {
              const isCompleted = currentStep > step.num;
              const isCurrent = currentStep === step.num;
              const Icon = step.icon;
              return (
                <div
                  key={step.num}
                  className={`flex flex-col items-center text-center p-2 rounded-xl transition-all ${
                    isCurrent
                      ? 'bg-blue-600/15 border border-blue-500/30'
                      : isCompleted
                      ? 'text-emerald-400'
                      : 'text-slate-500'
                  }`}
                >
                  <div
                    className={`w-9 h-9 rounded-xl flex items-center justify-center mb-1.5 font-bold text-xs transition-all ${
                      isCompleted
                        ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40'
                        : isCurrent
                        ? 'bg-gradient-to-tr from-blue-600 to-indigo-600 text-white shadow-md shadow-blue-500/30'
                        : 'bg-slate-800 text-slate-400'
                    }`}
                  >
                    {isCompleted ? <CheckCircle2 className="w-5 h-5" /> : <Icon className="w-4 h-4" />}
                  </div>
                  <span
                    className={`text-xs font-semibold ${
                      isCurrent ? 'text-white' : isCompleted ? 'text-emerald-400' : 'text-slate-400'
                    }`}
                  >
                    {step.title}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Error Alert */}
        {errorMsg && (
          <div className="mb-6 p-4 rounded-xl bg-red-950/50 border border-red-800/80 text-red-200 text-sm flex items-start gap-3 shadow-lg">
            <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-semibold text-red-300">Configuration Notice</h4>
              <p className="mt-0.5 text-xs text-red-200/90">{errorMsg}</p>
            </div>
            <button
              onClick={() => setErrorMsg('')}
              className="text-red-400 hover:text-red-300 text-xs font-medium"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Wizard Main Card */}
        <div className="bg-slate-900/90 backdrop-blur-xl border border-slate-800 rounded-3xl p-6 sm:p-10 shadow-2xl">
          {/* ----------------- STEP 1: SYSTEM READINESS ----------------- */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-bold text-white flex items-center gap-2.5">
                  <Cpu className="w-5 h-5 text-blue-400" />
                  System Readiness & Prerequisites
                </h2>
                <p className="text-xs sm:text-sm text-slate-400 mt-1">
                  We inspected your runtime environment and server settings to verify installation compatibility.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-4 rounded-2xl bg-slate-950/60 border border-slate-800/90">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-slate-400 font-medium">Node.js Runtime</span>
                    <span className="text-xs text-emerald-400 font-semibold flex items-center gap-1">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Compatible
                    </span>
                  </div>
                  <div className="text-lg font-bold font-mono text-white">
                    {statusData?.system?.nodeVersion || 'v18+'}
                  </div>
                  <div className="text-xs text-slate-400 mt-1">
                    Platform: {statusData?.system?.platform} ({statusData?.system?.arch})
                  </div>
                </div>

                <div className="p-4 rounded-2xl bg-slate-950/60 border border-slate-800/90">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-slate-400 font-medium">Memory Allocation</span>
                    <span className="text-xs text-emerald-400 font-semibold flex items-center gap-1">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Optimal
                    </span>
                  </div>
                  <div className="text-lg font-bold font-mono text-white">
                    {statusData?.system?.freeMemory} Free
                  </div>
                  <div className="text-xs text-slate-400 mt-1">
                    Total System RAM: {statusData?.system?.totalMemory}
                  </div>
                </div>

                <div className="p-4 rounded-2xl bg-slate-950/60 border border-slate-800/90">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-slate-400 font-medium">Database Layer</span>
                    {statusData?.dbConnected ? (
                      <span className="text-xs text-emerald-400 font-semibold flex items-center gap-1">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Connected
                      </span>
                    ) : (
                      <span className="text-xs text-red-400 font-semibold flex items-center gap-1">
                        <AlertTriangle className="w-3.5 h-3.5" /> Disconnected
                      </span>
                    )}
                  </div>
                  <div className="text-base font-bold text-white flex items-center gap-2">
                    <Database className="w-4 h-4 text-blue-400" />
                    Hostinger MySQL / Prisma ORM
                  </div>
                  <div className="text-xs text-slate-400 mt-1">
                    Status: {statusData?.dbConnected ? 'Ready for queries' : 'Connection failed'}
                  </div>
                </div>

                <div className="p-4 rounded-2xl bg-slate-950/60 border border-slate-800/90">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-slate-400 font-medium">Environment Mode</span>
                    <span className="text-xs text-blue-400 font-semibold uppercase">
                      {statusData?.system?.environment}
                    </span>
                  </div>
                  <div className="text-base font-bold text-white">
                    {statusData?.system?.environment === 'production'
                      ? 'Hostinger Production'
                      : 'Local Development'}
                  </div>
                  <div className="text-xs text-slate-400 mt-1">
                    Uptime: {statusData?.system?.uptime}s
                  </div>
                </div>
              </div>

              <div className="pt-4 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={fetchStatus}
                  className="px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white text-xs font-semibold transition flex items-center gap-1.5"
                >
                  <RefreshCw className="w-3.5 h-3.5" /> Re-check
                </button>
                <button
                  type="button"
                  onClick={() => setCurrentStep(2)}
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white text-sm font-semibold shadow-lg shadow-blue-600/30 flex items-center gap-2 transition"
                >
                  Continue to Database Check
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* ----------------- STEP 2: DATABASE CONNECTIVITY ----------------- */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-bold text-white flex items-center gap-2.5">
                  <Database className="w-5 h-5 text-blue-400" />
                  Database Connection & Table Readiness
                </h2>
                <p className="text-xs sm:text-sm text-slate-400 mt-1">
                  Verify that your Hostinger MySQL database is accessible and ready to store CMS content.
                </p>
              </div>

              <div className="p-5 rounded-2xl bg-slate-950/80 border border-slate-800/80 space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-slate-800">
                  <div>
                    <div className="text-sm font-semibold text-white">Live Connection Ping</div>
                    <div className="text-xs text-slate-400">
                      Executes an active query check against the configured database.
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleTestDb}
                    disabled={dbTesting}
                    className="px-4 py-2 rounded-xl bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 border border-blue-500/30 text-xs font-semibold flex items-center justify-center gap-2 transition disabled:opacity-50"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${dbTesting ? 'animate-spin' : ''}`} />
                    {dbTesting ? 'Testing...' : 'Test Connection'}
                  </button>
                </div>

                {dbTestResult && (
                  <div
                    className={`p-3.5 rounded-xl text-xs flex items-center gap-2.5 ${
                      dbTestResult.success
                        ? 'bg-emerald-950/40 border border-emerald-800 text-emerald-300'
                        : 'bg-red-950/40 border border-red-800 text-red-300'
                    }`}
                  >
                    {dbTestResult.success ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                    ) : (
                      <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />
                    )}
                    <span>{dbTestResult.message}</span>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <div className="p-3 bg-slate-900 rounded-xl border border-slate-800">
                    <span className="text-slate-400 block mb-1">Configured Database</span>
                    <span className="font-mono text-slate-200">Hostinger MySQL (u954394309_Cpanel)</span>
                  </div>
                  <div className="p-3 bg-slate-900 rounded-xl border border-slate-800">
                    <span className="text-slate-400 block mb-1">Prisma Client Driver</span>
                    <span className="font-mono text-slate-200">@prisma/client 5.18.0</span>
                  </div>
                </div>

                <div className="p-4 rounded-xl bg-blue-950/30 border border-blue-900/40 text-xs text-blue-200 leading-relaxed">
                  <p className="font-semibold text-blue-300 mb-1 flex items-center gap-1.5">
                    <Server className="w-3.5 h-3.5 text-blue-400" /> Hostinger Database Note:
                  </p>
                  If you imported <code className="bg-blue-950 px-1.5 py-0.5 rounded text-blue-100 font-mono">hostinger_database.sql</code> in Hostinger phpMyAdmin, all 20 required tables, indexes, and schemas are already created and verified.
                </div>
              </div>

              <div className="pt-4 flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={() => setCurrentStep(1)}
                  className="px-5 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white text-xs font-semibold transition flex items-center gap-1.5"
                >
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
                <button
                  type="button"
                  onClick={() => setCurrentStep(3)}
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white text-sm font-semibold shadow-lg shadow-blue-600/30 flex items-center gap-2 transition"
                >
                  Continue to Admin Setup
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* ----------------- STEP 3: SITE & ADMIN CONFIGURATION ----------------- */}
          {currentStep === 3 && (
            <form onSubmit={handleRunInstall} className="space-y-6">
              <div>
                <h2 className="text-xl font-bold text-white flex items-center gap-2.5">
                  <User className="w-5 h-5 text-blue-400" />
                  Site Identity & Super Administrator
                </h2>
                <p className="text-xs sm:text-sm text-slate-400 mt-1">
                  Configure your primary website branding and create the master Super Admin account with full permissions.
                </p>
              </div>

              {/* Site Info Section */}
              <div className="p-5 rounded-2xl bg-slate-950/60 border border-slate-800 space-y-4">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                  <Globe className="w-3.5 h-3.5 text-blue-400" /> Website Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-300 mb-1.5">
                      Site Name <span className="text-blue-400">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.siteName}
                      onChange={(e) => setFormData({ ...formData, siteName: e.target.value })}
                      className="w-full bg-slate-900 border border-slate-700/80 rounded-xl px-3.5 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition"
                      placeholder="FAJTech"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-300 mb-1.5">
                      Tagline / Motto
                    </label>
                    <input
                      type="text"
                      value={formData.tagline}
                      onChange={(e) => setFormData({ ...formData, tagline: e.target.value })}
                      className="w-full bg-slate-900 border border-slate-700/80 rounded-xl px-3.5 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition"
                      placeholder="Next-Gen IT & Cybersecurity"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-300 mb-1.5">
                      Contact / Inquiries Email
                    </label>
                    <input
                      type="email"
                      value={formData.contactEmail}
                      onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                      className="w-full bg-slate-900 border border-slate-700/80 rounded-xl px-3.5 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition"
                      placeholder="info@fajtech.com"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-300 mb-1.5">
                      Contact Phone
                    </label>
                    <input
                      type="text"
                      value={formData.contactPhone}
                      onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                      className="w-full bg-slate-900 border border-slate-700/80 rounded-xl px-3.5 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition"
                      placeholder="+1 (800) 555-0199"
                    />
                  </div>
                </div>
              </div>

              {/* Super Admin Section */}
              <div className="p-5 rounded-2xl bg-slate-950/60 border border-slate-800 space-y-4">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                  <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" /> Super Administrator Account
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-300 mb-1.5">
                      Admin Full Name <span className="text-blue-400">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        required
                        value={formData.adminName}
                        onChange={(e) => setFormData({ ...formData, adminName: e.target.value })}
                        className="w-full bg-slate-900 border border-slate-700/80 rounded-xl pl-10 pr-3.5 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition"
                        placeholder="Enterprise Admin"
                      />
                      <User className="w-4 h-4 text-slate-500 absolute left-3.5 top-3" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-300 mb-1.5">
                      Admin Email (Username) <span className="text-blue-400">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="email"
                        required
                        value={formData.adminEmail}
                        onChange={(e) => setFormData({ ...formData, adminEmail: e.target.value })}
                        className="w-full bg-slate-900 border border-slate-700/80 rounded-xl pl-10 pr-3.5 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition"
                        placeholder="admin@fajtech.com"
                      />
                      <Mail className="w-4 h-4 text-slate-500 absolute left-3.5 top-3" />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-300 mb-1.5">
                      Admin Password <span className="text-blue-400">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        required
                        minLength={8}
                        value={formData.adminPassword}
                        onChange={(e) => setFormData({ ...formData, adminPassword: e.target.value })}
                        className="w-full bg-slate-900 border border-slate-700/80 rounded-xl pl-10 pr-10 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition"
                        placeholder="Minimum 8 characters"
                      />
                      <Key className="w-4 h-4 text-slate-500 absolute left-3.5 top-3" />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3.5 top-3 text-slate-500 hover:text-slate-300"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>

                    {/* Password Strength Indicator */}
                    {formData.adminPassword && (
                      <div className="mt-2 flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-slate-800 rounded-full overflow-hidden flex gap-1">
                          {[1, 2, 3, 4].map((level) => (
                            <div
                              key={level}
                              className={`h-full flex-1 rounded-full ${
                                level <= strength.score ? strength.color : 'bg-slate-800'
                              }`}
                            />
                          ))}
                        </div>
                        <span className="text-[10px] text-slate-400 font-semibold">{strength.label}</span>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-300 mb-1.5">
                      Confirm Password <span className="text-blue-400">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        required
                        minLength={8}
                        value={formData.confirmPassword}
                        onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                        className="w-full bg-slate-900 border border-slate-700/80 rounded-xl pl-10 pr-10 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition"
                        placeholder="Repeat your password"
                      />
                      <Lock className="w-4 h-4 text-slate-500 absolute left-3.5 top-3" />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3.5 top-3 text-slate-500 hover:text-slate-300"
                      >
                        {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Options Checkbox */}
              <div className="p-4 rounded-xl bg-slate-950/50 border border-slate-800/80 flex items-start gap-3">
                <input
                  type="checkbox"
                  id="seedDemoData"
                  checked={formData.seedDemoData}
                  onChange={(e) => setFormData({ ...formData, seedDemoData: e.target.checked })}
                  className="mt-0.5 rounded border-slate-700 text-blue-600 focus:ring-blue-500 bg-slate-900"
                />
                <label htmlFor="seedDemoData" className="text-xs text-slate-300 select-none cursor-pointer">
                  <span className="font-semibold text-white block">Seed Essential Website Content</span>
                  Generate default homepage records, standard navigation menus, FAQ templates, and service definitions so your CMS is immediately ready to view.
                </label>
              </div>

              <div className="pt-4 flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={() => setCurrentStep(2)}
                  className="px-5 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white text-xs font-semibold transition flex items-center gap-1.5"
                >
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
                <button
                  type="submit"
                  disabled={installing}
                  className="px-7 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white text-sm font-semibold shadow-xl shadow-blue-600/30 flex items-center gap-2 transition disabled:opacity-50"
                >
                  {installing ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Running Installation...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      Install & Initialize CMS
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          {/* ----------------- STEP 4: INSTALLATION COMPLETED ----------------- */}
          {currentStep === 4 && (
            <div className="text-center space-y-6 py-4">
              <div className="inline-flex p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-3xl text-emerald-400 mb-2 animate-bounce">
                <CheckCircle2 className="w-12 h-12" />
              </div>

              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-emerald-400 bg-emerald-950/60 px-3 py-1 rounded-full border border-emerald-800/60">
                  Installation Successful
                </span>
                <h2 className="text-2xl sm:text-3xl font-black text-white tracking-tight mt-3">
                  Your CMS is Ready!
                </h2>
                <p className="text-slate-300 text-sm max-w-md mx-auto mt-2 leading-relaxed">
                  The Super Administrator account has been provisioned, site settings are active, and the database schema has been verified.
                </p>
              </div>

              <div className="max-w-md mx-auto bg-slate-950/80 border border-slate-800/80 rounded-2xl p-5 text-left space-y-3 text-xs">
                <div className="flex items-center justify-between pb-2 border-b border-slate-800">
                  <span className="text-slate-400">Super Admin Email:</span>
                  <span className="font-semibold text-white font-mono">{formData.adminEmail}</span>
                </div>
                <div className="flex items-center justify-between pb-2 border-b border-slate-800">
                  <span className="text-slate-400">Site Name:</span>
                  <span className="font-semibold text-white">{formData.siteName}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Security Status:</span>
                  <span className="text-emerald-400 font-semibold flex items-center gap-1">
                    <ShieldCheck className="w-3.5 h-3.5" /> Setup Wizard Locked
                  </span>
                </div>
              </div>

              <div className="pt-4 flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                <button
                  type="button"
                  onClick={() => navigate('/admin/login')}
                  className="flex-1 py-3.5 px-6 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-semibold text-sm shadow-xl shadow-blue-600/30 flex items-center justify-center gap-2 transition"
                >
                  <Lock className="w-4 h-4" />
                  Proceed to Admin Login
                </button>
                <Link
                  to="/"
                  className="py-3.5 px-6 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white font-semibold text-sm border border-slate-700 flex items-center justify-center gap-2 transition"
                >
                  <Globe className="w-4 h-4" />
                  View Website
                </Link>
              </div>
            </div>
          )}
        </div>

        {/* Footer Note */}
        <div className="mt-8 text-center text-xs text-slate-500 flex items-center justify-center gap-1.5">
          <ShieldCheck className="w-3.5 h-3.5 text-blue-500" />
          FAJTech Enterprise Content Management System • Production Grade
        </div>
      </div>
    </div>
  );
}
